import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface IframeWindow {
  id: number;
  siteId: number;
  title: string;
  url: string;
  faviconUrl?: string;
  minimized: boolean;
}

export const useUiStore = defineStore('ui', () => {
  // Dark mode
  const isDark = ref(false);

  // Sidebar
  const sidebarCollapsed = ref(false);
  const sidebarMobileOpen = ref(false);

  // Modals
  const showAddSiteModal = ref(false);
  const showEditSiteModal = ref(false);
  const editingSiteId = ref<number | null>(null);

  // Search
  const globalSearchOpen = ref(false);
  const globalSearchQuery = ref('');

  // Toast notifications
  const toasts = ref<Array<{ id: number; message: string; type: 'success' | 'error' | 'info' }>>([]);
  let toastId = 0;

  // Iframe windows
  const iframeWindows = ref<IframeWindow[]>([]);
  let iframeId = 0;

  const activeIframe = computed(() => iframeWindows.value.find((w) => !w.minimized));
  const minimizedIframes = computed(() => iframeWindows.value.filter((w) => w.minimized));
  const hasActiveIframe = computed(() => !!activeIframe.value);

  // Initialize dark mode from system/storage
  function initDarkMode(): void {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      isDark.value = stored === 'true';
    } else {
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    applyDarkMode();
  }

  function toggleDarkMode(): void {
    isDark.value = !isDark.value;
    localStorage.setItem('darkMode', String(isDark.value));
    applyDarkMode();
  }

  function applyDarkMode(): void {
    if (isDark.value) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  // Sidebar controls
  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value));
  }

  function toggleMobileSidebar(): void {
    sidebarMobileOpen.value = !sidebarMobileOpen.value;
  }

  function closeMobileSidebar(): void {
    sidebarMobileOpen.value = false;
  }

  function initSidebar(): void {
    const stored = localStorage.getItem('sidebarCollapsed');
    if (stored !== null) {
      sidebarCollapsed.value = stored === 'true';
    }
  }

  // Modal controls
  function openAddSiteModal(): void {
    showAddSiteModal.value = true;
  }

  function closeAddSiteModal(): void {
    showAddSiteModal.value = false;
  }

  function openEditSiteModal(siteId: number): void {
    editingSiteId.value = siteId;
    showEditSiteModal.value = true;
  }

  function closeEditSiteModal(): void {
    showEditSiteModal.value = false;
    editingSiteId.value = null;
  }

  // Search controls
  function openGlobalSearch(): void {
    globalSearchOpen.value = true;
  }

  function closeGlobalSearch(): void {
    globalSearchOpen.value = false;
    globalSearchQuery.value = '';
  }

  // Toast controls
  function showToast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    const id = ++toastId;
    toasts.value.push({ id, message, type });

    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }

  function removeToast(id: number): void {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  // Iframe window controls
  function openIframeWindow(siteId: number, title: string, url: string, faviconUrl?: string): void {
    // Check if site is already open
    const existing = iframeWindows.value.find((w) => w.siteId === siteId);
    if (existing) {
      // Restore if minimized
      existing.minimized = false;
      return;
    }

    // Minimize all other windows
    iframeWindows.value.forEach((w) => {
      w.minimized = true;
    });

    // Add new window
    iframeWindows.value.push({
      id: ++iframeId,
      siteId,
      title,
      url,
      faviconUrl,
      minimized: false,
    });
  }

  function closeIframeWindow(id: number): void {
    const index = iframeWindows.value.findIndex((w) => w.id === id);
    if (index !== -1) {
      iframeWindows.value.splice(index, 1);
    }
  }

  function minimizeIframeWindow(id: number): void {
    const window = iframeWindows.value.find((w) => w.id === id);
    if (window) {
      window.minimized = true;
    }
  }

  function restoreIframeWindow(id: number): void {
    // Minimize all others first
    iframeWindows.value.forEach((w) => {
      w.minimized = true;
    });
    // Restore the requested one
    const window = iframeWindows.value.find((w) => w.id === id);
    if (window) {
      window.minimized = false;
    }
  }

  function closeAllIframeWindows(): void {
    iframeWindows.value = [];
  }

  function openInNewTab(id: number): void {
    const window = iframeWindows.value.find((w) => w.id === id);
    if (window) {
      globalThis.open(window.url, '_blank');
    }
  }

  // Keyboard shortcuts
  function setupKeyboardShortcuts(): void {
    document.addEventListener('keydown', handleKeydown);
  }

  function cleanupKeyboardShortcuts(): void {
    document.removeEventListener('keydown', handleKeydown);
  }

  function handleKeydown(event: KeyboardEvent): void {
    // Ignore if typing in input
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      // Allow Escape to close modals/search
      if (event.key === 'Escape') {
        if (globalSearchOpen.value) {
          closeGlobalSearch();
        } else if (showAddSiteModal.value) {
          closeAddSiteModal();
        } else if (showEditSiteModal.value) {
          closeEditSiteModal();
        }
      }
      return;
    }

    // "/" to open search
    if (event.key === '/') {
      event.preventDefault();
      openGlobalSearch();
      return;
    }

    // Escape to close things
    if (event.key === 'Escape') {
      if (hasActiveIframe.value) {
        // Minimize active iframe
        const active = activeIframe.value;
        if (active) minimizeIframeWindow(active.id);
      } else if (globalSearchOpen.value) {
        closeGlobalSearch();
      } else if (showAddSiteModal.value) {
        closeAddSiteModal();
      } else if (showEditSiteModal.value) {
        closeEditSiteModal();
      } else if (sidebarMobileOpen.value) {
        closeMobileSidebar();
      }
      return;
    }

    // Alt+N pour ajouter un nouveau site (évite le conflit avec Ctrl+N du navigateur)
    if (event.altKey && event.key === 'n') {
      event.preventDefault();
      openAddSiteModal();
      return;
    }
  }

  return {
    // State
    isDark,
    sidebarCollapsed,
    sidebarMobileOpen,
    showAddSiteModal,
    showEditSiteModal,
    editingSiteId,
    globalSearchOpen,
    globalSearchQuery,
    toasts,
    iframeWindows,
    activeIframe,
    minimizedIframes,
    hasActiveIframe,

    // Actions
    initDarkMode,
    toggleDarkMode,
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    initSidebar,
    openAddSiteModal,
    closeAddSiteModal,
    openEditSiteModal,
    closeEditSiteModal,
    openGlobalSearch,
    closeGlobalSearch,
    showToast,
    removeToast,
    setupKeyboardShortcuts,
    cleanupKeyboardShortcuts,
    openIframeWindow,
    closeIframeWindow,
    minimizeIframeWindow,
    restoreIframeWindow,
    closeAllIframeWindows,
    openInNewTab,
  };
});
